import { useMemo, useState } from "react";
import {
  cases,
  categories,
  verdictOptions,
} from "@/mocks/melonData";
import type { Verdict } from "./brand";
import { verdictStyle, SectionHeading, SectionShell } from "./brand";

export default function CaseExplorer() {
  const [category, setCategory] = useState("全部");
  const [verdict, setVerdict] = useState<string>("全部");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cases.filter((c) => {
      if (category !== "全部" && c.category !== category) return false;
      if (verdict !== "全部" && c.verdict !== verdict) return false;
      if (!q) return true;
      const hay = `${c.title}${c.excerpt}${c.tags.join("")}${c.channels.join("")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [category, verdict, query]);

  return (
    <SectionShell id="cases" className="bg-background-100/60">
      <SectionHeading
        eyebrow="可信档案 · 实锤 / 存疑 / 辟谣"
        title="各路爆料，热度与真假一目了然"
        desc="同一套真相标准下的实时档案：实锤敢认、存疑敢等、辟谣敢追，每一栏都标注可信度与信源规模。"
      />

      {/* 顶部控制条 */}
      <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="scrollbar-none flex flex-1 items-center gap-2 overflow-x-auto pb-1">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setCategory(cat)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                category === cat
                  ? "bg-foreground-950 text-background-50"
                  : "bg-background-50 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <div className="flex items-center gap-1 rounded-full bg-background-50 p-1">
            {["全部", ...verdictOptions].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVerdict(v)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  verdict === v
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-200"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* 搜索 */}
      <div className="mt-5">
        <div className="relative">
          <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-secondary-700" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            type="search"
            aria-label="搜索爆料档案"
            placeholder="搜人物、事件或关键词，例如：代言、转会、数据造假…"
            className="w-full rounded-xl border border-background-200 bg-background-50 py-3 pl-11 pr-11 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition"
          />
          {query && (
            <button
              type="button"
              aria-label="清空搜索"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-background-100 text-secondary-800 hover:bg-background-200"
            >
              <i className="ri-close-line" />
            </button>
          )}
        </div>
      </div>

      <p className="mt-5 text-sm text-foreground-600">
        当前显示 <span className="font-semibold text-foreground-950">{filtered.length}</span>{" "}
        条档案
      </p>

      {/* 卡片网格 */}
      {filtered.length ? (
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          {filtered.map((c) => (
            <CaseCard key={c.id} cid={c.id} />
          ))}
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <i className="ri-emotion-sad-line text-4xl text-secondary-400" />
          <p className="mt-3 text-sm text-foreground-700">没有匹配的档案</p>
          <button
            type="button"
            onClick={() => {
              setCategory("全部");
              setVerdict("全部");
              setQuery("");
            }}
            className="mt-3 rounded-full bg-foreground-950 px-5 py-2 text-sm text-background-50 hover:bg-foreground-800"
          >
            清除筛选
          </button>
        </div>
      )}
    </SectionShell>
  );
}

function CaseCard({ cid }: { cid: string }) {
  const c = cases.find((x) => x.id === cid)!;
  const [open, setOpen] = useState(false);
  const st = verdictStyle(c.verdict);
  const v = c.verdict as Verdict;

  return (
    <article className="flex flex-col rounded-2xl border border-background-200 bg-background-50 p-5 hover:border-primary-200 transition-colors">
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold whitespace-nowrap ${st.pill}`}
        >
          {v === "实锤" ? (
            <i className="ri-check-double-line" />
          ) : v === "辟谣" ? (
            <i className="ri-forbid-2-line" />
          ) : (
            <i className="ri-time-line" />
          )}
          {c.verdict}
        </span>
        <span className="text-xs text-foreground-600">{c.category}</span>
        <span className="ml-auto text-xs text-secondary-800 whitespace-nowrap">
          {c.dateLabel}
        </span>
      </div>

      <div className="mt-4 flex items-start gap-3">
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border text-lg ${st.soft}`}
        >
          <i className={c.icon} />
        </span>
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground-950 leading-snug text-base">
            {c.title}
          </h3>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {c.tags.map((t) => (
              <span
                key={t}
                className="rounded-md bg-background-100 px-2 py-0.5 text-xs text-secondary-900 whitespace-nowrap"
              >
                #{t}
              </span>
            ))}
          </div>
        </div>
      </div>

      <p
        className={`mt-4 text-sm leading-relaxed text-foreground-700 ${
          open ? "" : "line-clamp-2"
        }`}
      >
        {c.excerpt}
      </p>

      {/* 展开区：证据渠道 */}
      <div
        className={`grid transition-all duration-300 ${
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="mt-4 rounded-xl bg-background-100 p-4">
            <p className="text-xs font-semibold text-foreground-800">
              已交叉信源 {c.sources} 处
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {c.channels.map((ch) => (
                <span
                  key={ch}
                  className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs whitespace-nowrap ${st.soft}`}
                >
                  <i className="ri-check-line" />
                  {ch}
                </span>
              ))}
            </div>
            <p className="mt-3 text-xs text-foreground-600">
              结论已留痕，如当事双方提交反证，将在 24 小时内触发二次复核。
            </p>
          </div>
        </div>
      </div>

      {/* 底部 */}
      <div className="mt-auto pt-4">
        <div className="flex items-center gap-3">
          <span className="text-xs text-foreground-600 whitespace-nowrap">
            内容可信度
          </span>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-200">
            <div
              className={`h-full rounded-full ${st.bar}`}
              style={{ width: `${c.score}%` }}
            />
          </div>
          <span className={`text-sm font-semibold ${st.label}`}>
            {c.score}
            <span className="text-xs text-foreground-500"> / 100</span>
          </span>
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-foreground-800 hover:text-primary-600 whitespace-nowrap"
        >
          {open ? "收起取证摘要" : "查看取证摘要"}
          <i className={open ? "ri-arrow-up-line" : "ri-arrow-down-line"} />
        </button>
      </div>
    </article>
  );
}